import ButtonDropdown from '@risk-smart/themed-cloudscape-components/button-dropdown';
import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { FormId } from '@risksmart-app/shared/forms/formConfigRegistry';
import { isTRPCClientError } from '@trpc/client';
import { type FC, type Ref, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FormEditorDialog,
  type FormEditorOutput,
  type FormEditorPreviewData,
  type FormPreviewHandle,
} from 'src/blocks/form-editor-dialog';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';

import { useObjectToolbarFormStore } from '@/blocks/object-toolbar/useObjectToolbarFormStore';
import { useCanManageFormConfig } from '@/hooks/queries/form-configuration/useCanManageFormConfig';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useIsModuleEnabled } from '@/hooks/useIsModuleEnabled';

import { useEditableFormContext } from './customisable-form/EditableFormContext';
import { useRiskSmartForm } from './customisable-form/RiskSmartFormContext';
import { useCustomisableFormDataContext } from './customisable-form-data/CustomisableFormDataContext';
import { LiveFormPreview } from './LiveFormPreview';
import { useIsInModal } from './modal-provider/ModalContext';
import { useFormEditorConditionalOptions } from './useFormEditorConditionalOptions';
import { useFormEditorInitialData } from './useFormEditorInitialData';
import { useFormEditorUpdate } from './useFormEditorUpdate';

// 5xx stays generic so no infrastructure detail leaks.
const getSaveFailureMessage = (error: unknown): string | undefined => {
  if (isTRPCClientError(error)) {
    const status = error.data?.httpStatus;

    return status !== undefined && status < 500 ? error.message : undefined;
  }

  return undefined;
};

interface FormMenuProps {
  parentType: FormId;
}
export const FormMenu: FC<FormMenuProps> = ({ parentType }) => {
  const { t } = useTranslation(['common']);
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');
  const formSectionsEnabled = useIsFeatureFlagEnabled('form_sections');
  const isAltValuesEnabled = useIsModuleEnabled('alt_values');
  const objectToolbarEnabled = useIsFeatureFlagEnabled('object_toolbar');
  const { canManage, loading: rbacLoading } =
    useCanManageFormConfig(parentType);
  const { hasPermission, loading: legacyLoading } = useHasPermissionQuery(
    'update:custom_attribute_schema'
  );

  const canShowMenu = trpcEnabled ? canManage : hasPermission;
  const loading = trpcEnabled ? rbacLoading : legacyLoading;

  // A toolbar is only actually present on pages that mount one. The
  // `object_toolbar` flag can be on globally while this page has no toolbar, in
  // which case we must NOT relocate — otherwise the 3-dot menu is hidden with
  // nothing to take its place, leaving blank space.
  const toolbarMounted = useObjectToolbarFormStore((s) => s.toolbarMounted);

  const inModal = useIsInModal();

  // When both the object toolbar and form sections are live AND a toolbar is
  // actually mounted on this page, the page-header toolbar takes over: its
  // overflow "Edit" group exposes "Edit form", so the 3-dot dropdown here is
  // suppressed and only the form editor it opens remains. On pages with no
  // toolbar, keep the dropdown so the control never vanishes.
  const relocateToToolbar =
    !inModal && // a modal's own dropdown never relocates behind the page toolbar
    objectToolbarEnabled &&
    formSectionsEnabled &&
    toolbarMounted &&
    canShowMenu &&
    !loading;

  // A modal never relocates (above), but under the same flag combo the legacy
  // "Add custom field" / "Edit form" controls are being retired rather than
  // relocated (CUS-293) — so a modal's own dropdown should drop them too and
  // keep only "Configure form". Gated on `toolbarMounted` like
  // `relocateToToolbar`: a modal opened from a page with no toolbar has
  // nowhere else to offer these, so it keeps all three rather than vanishing
  // them with nothing to take their place. Flag-off, the modal keeps all
  // three legacy items unchanged.
  const retireLegacyModalItems =
    inModal && objectToolbarEnabled && formSectionsEnabled && toolbarMounted;

  const { addNotification } = useNotifications();
  const { toggleEditMode } = useRiskSmartForm();
  const { showEditModal } = useEditableFormContext();

  const [configureOpen, setConfigureOpen] = useState(false);
  const setFormConfigState = useObjectToolbarFormStore(
    (s) => s.setFormConfigState
  );
  const clearFormConfigState = useObjectToolbarFormStore(
    (s) => s.clearFormConfigState
  );

  // Bridge the form editor opener up to the page-header toolbar (mounted outside
  // this provider). Published only in relocate mode; cleared on unmount / when
  // the flags or permission change, so a non-relocating FormMenu leaves the slot
  // null and the toolbar shows no "Edit form" action.
  useEffect(() => {
    if (!relocateToToolbar) {
      return;
    }
    setFormConfigState({ openConfigure: () => setConfigureOpen(true) });

    return () => clearFormConfigState();
  }, [relocateToToolbar, setFormConfigState, clearFormConfigState]);

  const { customAttributeSchema, formFieldConfigurations } =
    useCustomisableFormDataContext();

  const { initialData, fieldTypeOptions } =
    useFormEditorInitialData(parentType);

  const {
    fieldOptions: conditionalFieldOptions,
    getValueOptions,
    getFieldTypeMetadata,
  } = useFormEditorConditionalOptions(parentType, configureOpen);

  const save = useFormEditorUpdate({
    parentType,
    initialData,
    customAttributeSchema,
    formFieldConfigurations,
  });
  const [saving, setSaving] = useState(false);

  const onConfigureSave = (data: FormEditorOutput) => {
    // Set saving synchronously so the button disables on the next render
    // before any async work starts. Use .then()/.catch() instead of
    // async/await to keep this handler synchronous.
    setSaving(true);
    save(data)
      .then(() => {
        setConfigureOpen(false);
      })
      .catch((error: unknown) => {
        addNotification({
          type: 'error',
          content:
            getSaveFailureMessage(error) ??
            'Failed to save form configuration. Please try again.',
        });
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const renderPreview = useCallback(
    (
      data: FormEditorPreviewData,
      opts: { ref: Ref<FormPreviewHandle>; onDataChange: () => void }
    ) => (
      <LiveFormPreview
        ref={opts.ref}
        data={data}
        onDataChange={opts.onDataChange}
      />
    ),
    []
  );

  const onAddFieldAction = () => {
    showEditModal({
      parentType: parentType,
      fieldId: undefined,
      defaultRequired: undefined,
      defaultValueOptions: [],
      allowDefaultValue: true,
      forceRequired: undefined,
    });
  };

  const onEditAction = () => {
    toggleEditMode();
  };

  if (!canShowMenu || loading) {
    return null;
  }

  return (
    <>
      {relocateToToolbar ? null : (
        <ButtonDropdown
          data-testid={'form-settings-button'}
          expandToViewport
          ariaLabel={'Form Settings'}
          items={
            retireLegacyModalItems
              ? [
                  {
                    text: t('edit_form'),
                    id: 'configure',
                    disabled: false,
                  },
                ]
              : [
                  ...(formSectionsEnabled
                    ? [
                        {
                          text: t('configure_form'),
                          id: 'configure',
                          disabled: false,
                        },
                      ]
                    : []),
                  {
                    text: t('customAttributes.create_modal_title'),
                    id: 'add-field',
                    disabled: false,
                  },
                  {
                    text: t('edit_form'),
                    id: 'edit',
                    disabled: false,
                  },
                ]
          }
          variant={'icon'}
          onItemClick={(e) => {
            if (e.detail.id === 'configure') {
              setConfigureOpen(true);
            }
            if (e.detail.id === 'add-field') {
              onAddFieldAction();
            }
            if (e.detail.id === 'edit') {
              onEditAction();
            }
          }}
        >
          {'...'}
        </ButtonDropdown>
      )}
      {configureOpen ? (
        <FormEditorDialog
          open={configureOpen}
          onOpenChange={setConfigureOpen}
          initialData={initialData}
          fieldOptions={conditionalFieldOptions}
          getValueOptions={getValueOptions}
          getFieldTypeMetadata={getFieldTypeMetadata}
          isAltValuesEnabled={isAltValuesEnabled}
          onSave={onConfigureSave}
          saving={saving}
          renderPreview={renderPreview}
          fieldTypeOptions={fieldTypeOptions}
          lang={{
            formEditor: {
              // Translated here; the block stays i18n-agnostic.
              title: t('configure_form'),
              ...(formSectionsEnabled ? {} : { addSection: undefined }),
            },
          }}
        />
      ) : null}
    </>
  );
};
